import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Button,
  Alert,
  Fab,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import HomeIcon from "@mui/icons-material/Home";
import { posts as postsApi } from "../api/endpoints";
import type { Post } from "../types";
import PostCard from "../components/PostCard";
import AdSlot, { interleaveAds, isAdSlot } from "../components/AdSlot";
import CreatePost from "../components/CreatePost";
import PageHeader from "../components/PageHeader";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";

const PAGE_SIZE = 20;

export default function Feed() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [postList, setPostList] = useState<Post[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const loadPosts = useCallback(async (p: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await postsApi.list({ page: p, limit: PAGE_SIZE });
      if (p === 1) {
        setPostList(res.data ?? []);
      } else {
        setPostList((prev) => [...prev, ...(res.data ?? [])]);
      }
      if (res.meta) setTotalPages(res.meta.total_pages);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load posts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPosts(1);
  }, [loadPosts]);

  const handleLoadMore = () => {
    const next = page + 1;
    setPage(next);
    loadPosts(next);
  };

  const handleCreated = () => {
    setPage(1);
    loadPosts(1);
  };

  const itemsWithAds = interleaveAds(postList, 10);

  return (
    <>
    <PageHeader
      title={t("common.home")}
      subtitle={user ? `${t("feed.greeting")}, ${user.first_name}` : undefined}
      icon={<HomeIcon sx={{ color: "white", fontSize: 22 }} />}
    />
    <Container maxWidth="sm" sx={{ py: 2 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {postList.length === 0 && !loading && (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography color="text.secondary">{t("feed.noPosts")}</Typography>
        </Box>
      )}

      {itemsWithAds.map((item, idx) => {
        if (isAdSlot(item)) {
          return <AdSlot key={`ad-${item.__adIndex}`} index={item.__adIndex} />;
        }
        return <PostCard key={(item as Post).id} post={item as Post} onRefresh={() => loadPosts(1)} />;
      })}

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && page < totalPages && (
        <Box sx={{ textAlign: "center", py: 2 }}>
          <Button variant="outlined" onClick={handleLoadMore}>
            {t("feed.loadMore")}
          </Button>
        </Box>
      )}

      {/* Floating create button — writing requires an account */}
      {user && (
        <Fab
          color="primary"
          aria-label="create post"
          onClick={() => setCreateOpen(true)}
          sx={{ position: "fixed", bottom: 72, right: 16 }}
        >
          <AddIcon />
        </Fab>
      )}

      {user && (
        <CreatePost
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreated={handleCreated}
        />
      )}
    </Container>
    </>
  );
}
